import { useState, useRef, useId } from 'react'
import { FALLBACK_UNITS } from './useRecipeState'

/* ─── Helpers ─── */
function deriveKey(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
}

/* ─── Tag editor ─── */
const TagEditor = ({ tags, onChange }) => {
  const [inputVal, setInputVal] = useState('')

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputVal.trim()) {
      e.preventDefault()
      const next = inputVal.trim()
      if (!tags.includes(next)) onChange([...tags, next])
      setInputVal('')
    } else if (e.key === 'Backspace' && !inputVal && tags.length > 0) {
      onChange(tags.slice(0, -1))
    }
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', minHeight: 28 }}>
      {tags.map((t, i) => (
        <span key={i} className="tag">
          {t}
          <span
            className="tag-remove"
            onClick={() => onChange(tags.filter((_, j) => j !== i))}
            title="Remove tag"
          >
            &times;
          </span>
        </span>
      ))}
      <input
        type="text"
        value={inputVal}
        onChange={e => setInputVal(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={tags.length === 0 ? 'Add tag, press Enter' : ''}
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: 11,
          fontFamily: 'var(--font-ui)',
          color: 'var(--ink)',
          padding: '2px 4px',
          minWidth: 80,
          flexShrink: 0
        }}
      />
    </div>
  )
}

/* ─── Size class row ─── */
const SizeClassRow = ({ cls, index, prOptions, datalistId, onChange, onDelete, onConfirm }) => {
  const handlePrInput = (e) => {
    const val = e.target.value
    const match = prOptions.find(pr => `${pr.description} (${pr.baseRate} ${pr.unit}/hr)` === val)
    if (match) {
      onChange({ ...cls, productionRateId: match._id, productionRateHint: match.description })
    } else {
      onChange({ ...cls, productionRateHint: val, productionRateId: undefined })
    }
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0' }}>
      <input
        type="text"
        className="bare"
        value={cls.classKey || ''}
        onChange={e => onChange({ ...cls, classKey: e.target.value })}
        placeholder="key"
        style={{ width: 90, fontFamily: 'var(--font-mono)', fontSize: 11 }}
        title="Class key (e.g. small, 5gal)"
      />
      <input
        type="text"
        className="bare"
        value={cls.label || ''}
        onChange={e => onChange({ ...cls, label: e.target.value })}
        placeholder="Label"
        style={{ flex: 1 }}
        title="Display label"
      />
      <div style={{ flex: 2, position: 'relative' }}>
        <input
          type="text"
          className="bare"
          list={datalistId}
          value={cls.productionRateId
            ? (prOptions.find(p => p._id === cls.productionRateId)
              ? `${prOptions.find(p => p._id === cls.productionRateId).description} (${prOptions.find(p => p._id === cls.productionRateId).baseRate} ${prOptions.find(p => p._id === cls.productionRateId).unit}/hr)`
              : cls.productionRateHint || '')
            : (cls.productionRateHint || '')}
          onChange={handlePrInput}
          placeholder="Production rate..."
          style={{ fontSize: 12 }}
        />
        <datalist id={datalistId}>
          {prOptions.map(pr => (
            <option key={pr._id} value={`${pr.description} (${pr.baseRate} ${pr.unit}/hr)`} />
          ))}
        </datalist>
      </div>
      <button
        className="row-delete"
        title="Remove size class"
        onClick={() => onConfirm({
          title: 'Remove Size Class',
          message: `Remove size class "${cls.label || cls.classKey || 'this class'}"?`,
          actionLabel: 'Remove',
          onConfirm: onDelete
        })}
      >
        &times;
      </button>
    </div>
  )
}

/* ─── Single input slot card ─── */
const SlotCard = ({ slot, index, unitOptions, unitDatalistId, prOptions, prDatalistId, onChange, onDelete, onConfirm }) => {
  const handleLabelChange = (e) => {
    const newLabel = e.target.value
    if (slot._keyEdited) {
      onChange({ ...slot, label: newLabel })
    } else {
      onChange({ ...slot, label: newLabel, key: deriveKey(newLabel) })
    }
  }

  const handleKeyChange = (e) => {
    onChange({ ...slot, key: e.target.value, _keyEdited: true })
  }

  const handleKeyBlur = (e) => {
    // If user cleared key, re-derive from label and reset _keyEdited
    if (!e.target.value.trim()) {
      onChange({ ...slot, key: deriveKey(slot.label || ''), _keyEdited: false })
    }
  }

  const handleSizeClassChange = (idx, updated) => {
    const next = (slot.quantityClasses || []).map((c, i) => i === idx ? updated : c)
    onChange({ ...slot, quantityClasses: next })
  }

  const handleSizeClassDelete = (idx) => {
    onChange({ ...slot, quantityClasses: (slot.quantityClasses || []).filter((_, i) => i !== idx) })
  }

  const addSizeClass = () => {
    onChange({
      ...slot,
      quantityClasses: [
        ...(slot.quantityClasses || []),
        { classKey: '', label: '', productionRateId: null, productionRateHint: '' }
      ]
    })
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Card header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Label + key column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            type="text"
            className="input-field"
            value={slot.label || ''}
            onChange={handleLabelChange}
            placeholder="Input label (e.g. Total SF)"
          />
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--ink-4)',
            background: 'var(--bg-2)',
            padding: '2px 8px',
            borderRadius: 4,
            display: 'inline-block',
            alignSelf: 'flex-start'
          }}>
            key:&nbsp;
            <input
              type="text"
              value={slot.key || ''}
              onChange={handleKeyChange}
              onBlur={handleKeyBlur}
              style={{
                border: 'none',
                background: 'transparent',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-4)',
                outline: 'none',
                padding: 0,
                minWidth: 60,
                width: Math.max(60, (slot.key || '').length * 7)
              }}
              placeholder="auto"
            />
          </span>
        </div>

        {/* Unit */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-4)' }}>
            Unit
          </label>
          <input
            type="text"
            className="bare"
            list={unitDatalistId}
            value={slot.unit || ''}
            onChange={e => onChange({ ...slot, unit: e.target.value })}
            placeholder="SF"
            style={{ width: 72, textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 13 }}
          />
          <datalist id={unitDatalistId}>
            {unitOptions.map(u => <option key={u} value={u} />)}
          </datalist>
        </div>

        {/* Default value */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-4)' }}>
            Default
          </label>
          <input
            type="number"
            className="bare mono"
            value={slot.defaultValue ?? ''}
            onChange={e => onChange({ ...slot, defaultValue: e.target.value !== '' ? parseFloat(e.target.value) : null })}
            placeholder="—"
            style={{ width: 80, textAlign: 'right' }}
          />
        </div>

        {/* Delete */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingTop: 2 }}>
          <button
            className="row-delete"
            title="Remove input"
            onClick={() => onConfirm({
              title: 'Remove Input',
              message: `Remove input "${slot.label || slot.key || 'this input'}"? Components referencing this slot will lose their quantity source.`,
              actionLabel: 'Remove',
              onConfirm: onDelete
            })}
          >
            &times;
          </button>
        </div>
      </div>

      {/* Tags row */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span className="section-label">Tags</span>
        <TagEditor
          tags={slot.tags || []}
          onChange={tags => onChange({ ...slot, tags })}
        />
      </div>

      {/* Size classes toggle */}
      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={!!slot.isSizeClassInput}
            onChange={e => onChange({ ...slot, isSizeClassInput: e.target.checked })}
            style={{ accentColor: 'var(--accent)', width: 14, height: 14, cursor: 'pointer' }}
          />
          <span style={{ fontWeight: 500, color: slot.isSizeClassInput ? 'var(--accent)' : 'var(--ink-3)' }}>
            Has Size Classes
          </span>
          {slot.isSizeClassInput && (slot.quantityClasses || []).length > 0 && (
            <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-soft)', padding: '1px 6px', borderRadius: 10 }}>
              {(slot.quantityClasses || []).length}
            </span>
          )}
        </label>

        {slot.isSizeClassInput && (
          <div style={{ marginTop: 10, paddingLeft: 8, borderLeft: '2px solid var(--primary)' }}>
            {/* Column headers */}
            {(slot.quantityClasses || []).length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 2, paddingBottom: 4, borderBottom: '1px solid var(--line)' }}>
                <span style={{ width: 90, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-4)' }}>Key</span>
                <span style={{ flex: 1, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-4)' }}>Label</span>
                <span style={{ flex: 2, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-4)' }}>Production Rate</span>
                <span style={{ width: 24 }} />
              </div>
            )}

            {/* Class rows */}
            {(slot.quantityClasses || []).map((cls, ci) => (
              <SizeClassRow
                key={ci}
                cls={cls}
                index={ci}
                prOptions={prOptions}
                datalistId={`${prDatalistId}-${ci}`}
                onChange={updated => handleSizeClassChange(ci, updated)}
                onDelete={() => handleSizeClassDelete(ci)}
                onConfirm={onConfirm}
              />
            ))}

            {/* Add class button */}
            <button
              className="btn-ghost btn"
              onClick={addSizeClass}
              style={{ marginTop: 8, padding: '6px 12px', fontSize: 12, width: 'auto', justifyContent: 'flex-start' }}
            >
              + class
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   InputsTab
   ═══════════════════════════════════════════════════════════ */
const InputsTab = ({ slots, setSlots, productionRates, onConfirm }) => {
  const uid = useId()

  // Derived unit options from production rates + fallback
  const unitOptions = [
    ...new Set([
      ...FALLBACK_UNITS,
      ...(productionRates || []).map(pr => pr.unit).filter(Boolean)
    ])
  ].sort()

  const addSlot = () => {
    setSlots([
      ...(slots || []),
      { key: `input_${Date.now()}`, label: '', unit: 'LF', defaultValue: null, tags: [], _keyEdited: false }
    ])
  }

  const updateSlot = (index, updated) => {
    setSlots((slots || []).map((s, i) => i === index ? updated : s))
  }

  const deleteSlot = (index) => {
    setSlots((slots || []).filter((_, i) => i !== index))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div className="section-title">
            Input Requirements
            {(slots || []).length > 0 && (
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-4)', marginLeft: 8 }}>
                ({(slots || []).length})
              </span>
            )}
          </div>
          <div className="section-subtitle">Define the quantity inputs needed to estimate this service</div>
        </div>
        <button className="btn btn-ghost" onClick={addSlot} style={{ width: 'auto', padding: '8px 16px' }}>
          + Add Input
        </button>
      </div>

      {/* Empty state */}
      {!(slots || []).length && (
        <div className="empty-state">
          <div className="empty-icon">&#x1F4CF;</div>
          <div className="empty-title">No input requirements yet</div>
          <div className="empty-subtitle">Add inputs like "Total SF" or "Linear Footage" to power formula-based pricing</div>
        </div>
      )}

      {/* Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(slots || []).map((slot, i) => (
          <SlotCard
            key={slot.key || i}
            slot={slot}
            index={i}
            unitOptions={unitOptions}
            unitDatalistId={`${uid}-units-${i}`}
            prOptions={productionRates || []}
            prDatalistId={`${uid}-pr-${i}`}
            onChange={updated => updateSlot(i, updated)}
            onDelete={() => deleteSlot(i)}
            onConfirm={onConfirm}
          />
        ))}
      </div>

      {/* Footer add button (shown only when cards exist) */}
      {(slots || []).length > 0 && (
        <button className="btn btn-ghost" onClick={addSlot}>
          + Add Input
        </button>
      )}
    </div>
  )
}

export default InputsTab
